import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Fuel, Check, X } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

const FUEL_TYPES = ["diesel", "petrol", "hybrid", "phev", "lpg", "electric"];

/** Inline fuel setup for one vehicle. Both fields are optional: leave them empty and this
 *  vehicle simply contributes no fuel cost — nothing is estimated behind the manager's back. */
export default function FuelEditor({ vehicle, canEdit }: { vehicle: any; canEdit: boolean }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [fuelType, setFuelType] = useState<string>(vehicle.fuelType || "");
  const [consumption, setConsumption] = useState<string>(
    vehicle.consumptionLper100km != null ? String(vehicle.consumptionLper100km) : "");

  const econ = useQuery({
    queryKey: ["vehicle-econ", vehicle.id],
    queryFn: async () => (await api.get(`/vehicles/${vehicle.id}/economics`)).data,
    enabled: open,
  });

  const save = useMutation({
    mutationFn: () => api.patch(`/vehicles/${vehicle.id}`, {
      fuelType: fuelType || null,
      consumptionLper100km: consumption ? Number(consumption) : null,
    }),
    onSuccess: (r: any) => {
      qc.invalidateQueries({ queryKey: ["vehicles"] });
      qc.invalidateQueries({ queryKey: ["vehicle-econ", vehicle.id] });
      const c = r.data?.consumptionResolved;
      toast.success(c ? `${vehicle.plate}: ${c.litresPer100km} L/100km (${c.source})` : `${vehicle.plate}: no fuel cost will be calculated`);
      setOpen(false);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  if (!open) {
    return (
      <button data-testid={`fuel-open-${vehicle.plate}`} onClick={() => canEdit && setOpen(true)} disabled={!canEdit}
              className={cn("flex items-center gap-1.5 font-mono text-[10px] border rounded px-1.5 py-0.5",
                            vehicle.fuelType ? "border-[#26334d] text-emerald-400" : "border-amber-500/30 text-amber-400",
                            canEdit && "hover:bg-[#182030]")}>
        <Fuel className="h-3 w-3" />
        {vehicle.fuelType
          ? `${vehicle.fuelType}${vehicle.consumptionLper100km ? ` · ${vehicle.consumptionLper100km} L` : ""}`
          : "no fuel data"}
      </button>
    );
  }

  return (
    <div data-testid={`fuel-editor-${vehicle.plate}`} className="border border-[#26334d] rounded-md p-2 bg-[#0d1220] space-y-2 min-w-[240px]">
      <div className="flex flex-wrap gap-1">
        {FUEL_TYPES.map((f) => (
          <button key={f} onClick={() => setFuelType(fuelType === f ? "" : f)} data-testid={`fuel-type-${vehicle.plate}-${f}`}
                  className={cn("font-mono text-[10px] px-1.5 py-0.5 rounded border",
                                fuelType === f ? "border-blue-500/50 bg-blue-500/20 text-blue-300" : "border-[#26334d] text-slate-400 hover:text-slate-200")}>{f}</button>
        ))}
      </div>
      <label className="block text-[10px] text-slate-500">
        L/100 km <span className="text-slate-600">(optional — overrides the catalogue estimate)</span>
        <Input value={consumption} onChange={(e) => setConsumption(e.target.value)} placeholder="e.g. 7.4" inputMode="decimal"
               data-testid={`fuel-consumption-${vehicle.plate}`}
               className="mt-1 h-7 bg-[#0f141f] border-[#26334d] font-mono text-xs" />
      </label>
      {econ.data?.consumption && (
        <div className="font-mono text-[10px] text-slate-500 leading-relaxed">
          {econ.data.consumption.litresPer100km} L/100km · {econ.data.consumption.source}
          {econ.data.consumption.estimated && <span className="text-amber-400"> · estimate</span>}
          {econ.data.costPerKm != null && <div className="text-slate-400">{econ.data.costPerKm} {econ.data.currency}/km at today&apos;s pump price</div>}
        </div>
      )}
      {econ.data && !econ.data.canCost && (
        <div className="font-mono text-[10px] text-amber-400">No cost can be calculated yet.</div>
      )}
      <div className="flex gap-1">
        <Button size="sm" data-testid={`fuel-save-${vehicle.plate}`} disabled={save.isPending} onClick={() => save.mutate()}
                className="h-6 text-[10px] bg-blue-600 hover:bg-blue-500 text-white"><Check className="h-3 w-3 mr-1" />Save</Button>
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)} className="h-6 text-[10px] text-slate-400"><X className="h-3 w-3" /></Button>
      </div>
    </div>
  );
}
